import { Box, BoxProps, Text } from 'grommet';
import { Alert } from 'grommet-icons';
import { useEffect, useRef, useState } from 'react';

export enum SaveStatus {
  Saving = 'saving',
  Unsaved = 'unsaved',
  Saved = 'saved',
  Error = 'error',
}

export type SaveStatusIndicatorProps = {
  saveStatus?: SaveStatus;
  boxProps?: BoxProps;
};

/** How long "Saved" shows after a save before it goes away */
const SAVED_SHOWN_MS = 2000;

/**
 * A quiet save status: a static grey "Saving…", then "Saved" for a couple of seconds after a save
 * (not on load), and a red error that stays until the next save works.
 */
export const SaveStatusIndicator = ({ saveStatus, boxProps }: SaveStatusIndicatorProps) => {
  const previous = useRef(saveStatus);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    const was = previous.current;
    previous.current = saveStatus;
    if (saveStatus !== SaveStatus.Saved || was === undefined || was === SaveStatus.Saved) {
      setJustSaved(false);
      return;
    }
    setJustSaved(true);
    const timer = setTimeout(() => setJustSaved(false), SAVED_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const content =
    saveStatus === SaveStatus.Error ? (
      <>
        <Alert size="12px" color="status-error" />
        <Text size="13px" color="status-error">
          Error saving changes
        </Text>
      </>
    ) : saveStatus === SaveStatus.Saving || saveStatus === SaveStatus.Unsaved ? (
      <Text size="13px" color="text-meta">
        Saving…
      </Text>
    ) : saveStatus === SaveStatus.Saved && justSaved ? (
      <Text size="13px" color="text-meta" className="fade-out-late">
        Saved
      </Text>
    ) : null;

  // Mounted whenever there's a status, so screen readers hear what it changes to
  if (!saveStatus) return null;

  return (
    <Box direction="row" gap="xsmall" align="center" margin={{ left: 'medium' }} aria-live="polite" {...boxProps}>
      {content}
    </Box>
  );
};
